import { toast } from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';

import { request } from '@core/api/request';
import type { TBankAccount } from '@shared/types/bank-account';
import { useParams } from 'react-router-dom';
import { queryClient } from '@core/api/query-client';
import { QUERY_KEYS } from '@src/shared/constants/query-keys';
import type { IInitialBankValues } from '../edit-bank-info/interfaces';

export const createBankInfo = async ({
  ...values
}: IInitialBankValues): Promise<TBankAccount> => {
  const formData = new FormData();

  if (values.logo) {
    const modifiedAvatar =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (values.logo as any) instanceof File
        ? values.logo
        : new Blob([values.logo], {
            type: 'image/png',
          });
    formData.append('logo', modifiedAvatar);
  }
  formData.append('bank_name', values.bank_name);
  formData.append('account_number', values.account_number.toString());
  formData.append('beneficiary_name', values.beneficiary_name);
  formData.append('address_line_1', values.address_line_1);
  formData.append('address_line_2', values.address_line_2 || '');
  formData.append('city', values.city || '');
  formData.append('country', (values.country as string) || '');
  formData.append('state', values.state || '');
  formData.append('postal_code', values.postal_code || '');
  formData.append('swift_code', values.swift_code);
  formData.append('iban', values.iban || '');
  formData.append('currency', values.currency);
  return await request({
    url: 'bank-accounts/',
    method: 'POST',
    body: formData,
  });
};

export const useCreateBankInfo = () => {
  const { organizationSlug } = useParams();
  return useMutation({
    mutationFn: createBankInfo,
    onError: (error: string) => {
      toast.error(error);
    },
    onSuccess: () => {
      toast.success('Bank info has created successfully.');
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.getBankInfo, organizationSlug],
      });
    },
  });
};
